// services/traitService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Trait, TraitForm } from "../types";
import { getTraitCategoryById } from "./traitCategoryService";

const TRAITS_COLLECTION = "traits";

export const getTraits = async (categoryId?: string): Promise<Trait[]> => {
  try {
    let q;
    if (categoryId) {
      q = query(collection(db, TRAITS_COLLECTION), where("categoryId", "==", categoryId), orderBy("name", "asc"));
    } else {
      q = query(collection(db, TRAITS_COLLECTION), orderBy("name", "asc"));
    }
    
    const querySnapshot = await getDocs(q);
    const traits = await Promise.all(querySnapshot.docs.map(async (doc) => {
      const data = doc.data();
      let categoryName = data.categoryName;
      
      if (!categoryName && data.categoryId) {
        const category = await getTraitCategoryById(data.categoryId);
        categoryName = category?.name || "";
      }
      
      return {
        id: doc.id,
        name: data.name,
        categoryId: data.categoryId,
        categoryName,
        scaleType: data.scaleType,
        minScore: data.minScore,
        maxScore: data.maxScore,
        lowRangeMin: data.lowRangeMin,
        lowRangeMax: data.lowRangeMax,
        mediumRangeMin: data.mediumRangeMin,
        mediumRangeMax: data.mediumRangeMax,
        highRangeMin: data.highRangeMin,
        highRangeMax: data.highRangeMax,
        interpretationRules: data.interpretationRules,
        weightage: data.weightage,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as Trait;
    }));
    
    return traits;
  } catch (error) {
    console.error("Error fetching traits:", error);
    throw error;
  }
};

export const getTraitById = async (id: string): Promise<Trait | null> => {
  try {
    const docRef = doc(db, TRAITS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      let categoryName = data.categoryName;
      
      if (!categoryName && data.categoryId) {
        const category = await getTraitCategoryById(data.categoryId);
        categoryName = category?.name || "";
      }
      
      return {
        id: docSnap.id,
        name: data.name,
        categoryId: data.categoryId,
        categoryName,
        scaleType: data.scaleType,
        minScore: data.minScore,
        maxScore: data.maxScore,
        lowRangeMin: data.lowRangeMin,
        lowRangeMax: data.lowRangeMax,
        mediumRangeMin: data.mediumRangeMin,
        mediumRangeMax: data.mediumRangeMax,
        highRangeMin: data.highRangeMin,
        highRangeMax: data.highRangeMax,
        interpretationRules: data.interpretationRules,
        weightage: data.weightage,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as Trait;
    }
    return null;
  } catch (error) {
    console.error("Error fetching trait:", error);
    throw error;
  }
};

export const createTrait = async (traitData: TraitForm): Promise<string> => {
  try {
    let categoryName = "";
    if (traitData.categoryId) {
      const category = await getTraitCategoryById(traitData.categoryId);
      categoryName = category?.name || "";
    }
    
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, TRAITS_COLLECTION), {
      name: traitData.name,
      categoryId: traitData.categoryId,
      categoryName,
      scaleType: traitData.scaleType,
      minScore: traitData.minScore,
      maxScore: traitData.maxScore,
      lowRangeMin: traitData.lowRangeMin,
      lowRangeMax: traitData.lowRangeMax,
      mediumRangeMin: traitData.mediumRangeMin,
      mediumRangeMax: traitData.mediumRangeMax,
      highRangeMin: traitData.highRangeMin,
      highRangeMax: traitData.highRangeMax,
      interpretationRules: traitData.interpretationRules,
      weightage: traitData.weightage,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating trait:", error);
    throw error;
  }
};

export const updateTrait = async (id: string, traitData: Partial<TraitForm>): Promise<void> => {
  try {
    const updateData: any = { 
      name: traitData.name,
      categoryId: traitData.categoryId,
      scaleType: traitData.scaleType,
      minScore: traitData.minScore,
      maxScore: traitData.maxScore,
      lowRangeMin: traitData.lowRangeMin,
      lowRangeMax: traitData.lowRangeMax,
      mediumRangeMin: traitData.mediumRangeMin,
      mediumRangeMax: traitData.mediumRangeMax,
      highRangeMin: traitData.highRangeMin,
      highRangeMax: traitData.highRangeMax,
      interpretationRules: traitData.interpretationRules,
      weightage: traitData.weightage,
      updatedAt: Timestamp.now() 
    };
    
    if (traitData.categoryId) {
      const category = await getTraitCategoryById(traitData.categoryId);
      updateData.categoryName = category?.name || "";
    }
    
    const docRef = doc(db, TRAITS_COLLECTION, id);
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating trait:", error);
    throw error;
  }
};

export const deleteTrait = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, TRAITS_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting trait:", error);
    throw error;
  }
};